import Icon from '@/components/Icon';
import { SelectField } from '@/components/fields';
import NumField from '@/components/fields/NumField';
import { ISelectFieldOption } from '@/components/fields/SelectField';
import { confirmModal } from '@/components/modal/Modal';
import { useKeycloak } from '@/contexts/KeycloakContext';
import { useCapacityTypes, useEpvFamilies } from '@/hooks/api';
import { useSliderRange } from '@/hooks/useSliderRange';
import { Row, Table } from '@/styles/common';
import {
	ScenarioContentContainer,
	ScenarioMainContent,
} from '@/styles/scenario';
import axios from '@/utils/axios';
import {
	CURRENT_YEAR,
	YEARS_TO_ADD,
	YEARS_TO_SUBTRACT,
	convertScenariosToList,
	currentYear,
	isInRange,
	joinClassNames,
	sortCapacityScenarios,
} from '@/utils/utils';
import Tippy from '@tippyjs/react';
import { produce } from 'immer';
import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useTheme } from 'styled-components';
import CommonScenarioHeader from '../CommonScenarioHeader';
import CapacityChart from './CapacityChart';
import CapacityConfig from './CapacityConfig';
import CapacityTree from './CapacityTree';
import type {Theme as ThemeType} from '@/styles/Themes'
interface CapacityProps {}

const getEmptyCapacity = (
	data: TCapacityScenarioData
): TCapacityScenarioData => {
	const values = Object.keys(data.values).reduce(
		(acc, year) => ({
			...acc,
			[year]: {
				origin: '',
				value: null,
			},
		}),
		{}
	);
	return {
		...data,
		capacity_origin: '',
		capacity_type: '',
		epv_family_lvl2_ids: [],
		epv_family_lvl2_names: [],
		values,
	};
};

const Capacity: React.FC<CapacityProps> = () => {
	const { keycloak } = useKeycloak();
	const [scenarioId, setScenarioId] = useState<TValue>();
	const [isAdding, setIsAdding] = useState(false);

	const queryClient = useQueryClient();

	const { data: epvFamilies } = useEpvFamilies();
	const {
		query: { data: capacityTypes },
		order: capacityTypesOrder,
	} = useCapacityTypes();

	const [scenarioData, setScenarioData] = useState<TCapacityScenarioData[]>([]);

	const theme = useTheme() as ThemeType;

	const { data: scenarios } = useQuery(
		['capacity_scenarios'],
		() => axios.get<TScenario[]>('/capacity_scenarios'),
		{
			select: ({ data }) => convertScenariosToList(data),
		}
	);

	const {
		value: yearRange,
		setValue: setYearRange,
		min,
		max,
	} = useSliderRange({
		currentValues: [CURRENT_YEAR, CURRENT_YEAR + YEARS_TO_ADD],
		min: Math.max(2019, CURRENT_YEAR - YEARS_TO_SUBTRACT),
		max: CURRENT_YEAR + YEARS_TO_ADD,
	});

	const [selectedItem, setSelectedItem] = useState<TCapacityTreeSelectedItem>();

	const isProductionRegionSelected = selectedItem?.depth === 1;

	const { data: scenario } = useQuery(
		['capacity_scenario', scenarioId, selectedItem?.value, yearRange],
		() =>
			axios.get<TCapacityScenarioData[]>('/capacity_scenario', {
				params: {
					scenario_id: scenarioId,
					production_region: selectedItem!.item.production_region,
					...(!isProductionRegionSelected && {
						plant_code: selectedItem!.item.plant_code,
					}),
					year_start: yearRange[0],
					year_end: yearRange[1],
				},
			}),
		{
			enabled: !!scenarioId && !!selectedItem?.item,
			select: ({ data }) => sortCapacityScenarios(data, capacityTypesOrder),
			cacheTime: 0,
		}
	);

	const formattedScenario = useMemo((): TCapacityChartData[] => {
		if (!scenarioData) return [];
		return scenarioData.map((item, index) => {
			const capacityType = capacityTypes?.[item.capacity_type]?.name;
			return {
				name: capacityType ? `${capacityType} (${index + 1})` : undefined,
				values: item.values,
			};
		});
	}, [scenarioData]);

	const formattedScenarioWithYear = useMemo(() => {
		return structuredClone(formattedScenario).map((row) => {
			Object.keys(row.values).map((year) => {
				if (!isInRange(+year, yearRange)) {
					delete row.values[+year];
				}
			});
			return row;
		});
	}, [formattedScenario, yearRange]);

	const { mutate: updateCapacity } = useMutation(
		['update_capacity', yearRange],
		({
			year,
			capacity,
			capacity_type,
			epv_families,
		}: {
			year: number;
			capacity: number;
			capacity_type: string;
			epv_families: number[];
		}) =>
			axios.post('/capacity_updates', {
				capacity_scenario_id: scenarioId,
				last_modified_by: keycloak?.tokenParsed?.preferred_username,
				last_modified_datetime: new Date(),
				production_region: selectedItem!.item.production_region,
				...(!isProductionRegionSelected && {
					plant_code: selectedItem!.item.plant_code,
				}),
				year,
				capacity,
				capacity_type,
				epv_family_lvl2_ids: epv_families,
				year_start: yearRange[0],
				year_end: yearRange[1],
			}),
		{
			onSuccess: ({ data }) => {
				setScenarioData(sortCapacityScenarios(data, capacityTypesOrder));
			},
		}
	);

	const { mutate: deleteCapacityUpdate } = useMutation(
		['delete_capacity_update'],
		({
			capacity_type,
			epv_families,
		}: {
			capacity_type: string;
			epv_families: number[];
		}) =>
			axios.post('/capacity_updates', {
				capacity_scenario_id: scenarioId,
				last_modified_by: keycloak?.tokenParsed?.preferred_username,
				last_modified_datetime: new Date(),
				production_region: selectedItem!.item.production_region,
				...(!isProductionRegionSelected && {
					plant_code: selectedItem!.item.plant_code,
				}),
				epv_family_lvl2_ids: epv_families,
				capacity_type,
			}),
		{
			onSuccess: ({ data }) => {
				setScenarioData(sortCapacityScenarios(data, capacityTypesOrder));
			},
		}
	);

	const { mutate: createCapacity } = useMutation(
		['create_capacity'],
		({
			capacity_type,
			epv_family_ids,
		}: {
			capacity_type: string;
			epv_family_ids: number[];
		}) =>
			axios.post('/capacity_updates', {
				capacity_scenario_id: scenarioId,
				last_modified_by: keycloak?.tokenParsed?.preferred_username,
				last_modified_datetime: new Date(),
				production_region: selectedItem!.item.production_region,
				...(!isProductionRegionSelected && {
					plant_code: selectedItem!.item.plant_code,
				}),
				year: currentYear(),
				capacity: 0,
				capacity_type,
				epv_family_lvl2_ids: epv_family_ids,
			}),
		{
			onSuccess: ({ data }) => {
				queryClient.invalidateQueries([
					'capacity_scenario',
					scenarioId,
					selectedItem?.value,
					yearRange,
				]);
				setIsAdding(false);
				setScenarioData(sortCapacityScenarios(data, capacityTypesOrder));
			},
		}
	);

	const { mutate: resetCapacity } = useMutation(
		['reset_capacity'],
		(epvFamiliesIds: number[]) =>
			axios.post('/capacity_updates', {
				capacity_scenario_id: scenarioId,
				last_modified_by: keycloak?.tokenParsed?.preferred_username,
				last_modified_datetime: new Date(),
				production_region: selectedItem!.item.production_region,
				...(!isProductionRegionSelected && {
					plant_code: selectedItem!.item.plant_code,
				}),
				epv_family_lvl2_ids: epvFamiliesIds,
			}),
		{
			onSuccess: ({ data }) => {
				setScenarioData(sortCapacityScenarios(data, capacityTypesOrder));
			},
		}
	);

	useEffect(() => {
		setIsAdding(false);
		if (scenario) {
			setScenarioData(sortCapacityScenarios(scenario, capacityTypesOrder));
		}
	}, [scenario]);

	const getEpvFamiliesDependingOnCapacityType = (
		capacityType: string
	): ISelectFieldOption[] => {
		return capacityType === ''
			? []
			: epvFamilies
					?.filter((epv) => epv[capacityType as keyof TProcess] !== 0)
					.map((epv) => ({
						label: epv.epv_family_lvl2,
						value: epv.epv_family_lvl2_id,
					})) ?? [];
	};

	const getEpvFamiliesByIds = (ids: number[]) => {
		return epvFamilies
			?.filter((epv) => ids.includes(epv.epv_family_lvl2_id))
			.map((epv) => epv.epv_family_lvl2);
	};

	const years = useMemo(
		() =>
			Array.from(
				{ length: yearRange[1] - yearRange[0] + 1 },
				(_, i) => i + yearRange[0]
			),
		[yearRange]
	);

	return (
		<div>
			<CommonScenarioHeader
				scenarioId={scenarioId}
				setScenarioId={setScenarioId}
				scenarios={scenarios}
				type='capacity'
			/>
			<ScenarioContentContainer>
				<ScenarioMainContent>
					<CapacityTree
						scenarioId={scenarioId}
						selectedItem={selectedItem}
						setSelectedItem={setSelectedItem}
					/>
					<CapacityChart
						scenario={formattedScenarioWithYear}
						title={(selectedItem?.value as string) ?? ''}
					/>
				</ScenarioMainContent>
				<CapacityConfig
					setScenarioData={setScenarioData}
					scenarioId={scenarioId}
					selectedItem={selectedItem}
					scenarios={scenarios}
					isProductionRegionSelected={isProductionRegionSelected}
					min={min}
					max={max}
					yearRange={yearRange}
					setYearRange={setYearRange}
					currentScenarioRange={null}
				/>
				{selectedItem?.item && (
					<Table hover>
						<thead>
							<tr>
								<th>
									<Icon
										icon={isAdding ? 'xmark' : 'add'}
										style={{ cursor: 'pointer' }}
										onClick={() => {
											setIsAdding((prev) => !prev);
											if (isAdding) {
												setScenarioData((prev) => prev.slice(0, -1));
											} else {
												setScenarioData((prev) => [
													...prev,
													getEmptyCapacity(
														prev.length > 0
															? prev[0]
															: {
																	values: {},
																	capacity_origin: '',
																	capacity_type: '',
																	country: '',
																	epv_family_lvl2_ids: [],
																	plant_code: '',
																	production_region: '',
																	epv_family_lvl2_names: [],
																	global_region: '',
																	plant: '',
																	sap_code: '',
															  }
													),
												]);
											}
										}}
									/>
								</th>
								<th></th>
								<th>Unit</th>
								<th>EPV2 Families</th>
								{years.map((year) => (
									<th key={year}>{year}</th>
								))}
							</tr>
						</thead>
						<tbody>
							{formattedScenarioWithYear?.map((row, rowIndex) => {
								const data = scenarioData![rowIndex];
								const capacityType = capacityTypes![data.capacity_type];
								const isVirtualCapacity =
									(isProductionRegionSelected &&
										data.capacity_origin === 'Capacité virtuelle régionale') ||
									(!isProductionRegionSelected &&
										data.capacity_origin === 'Capacité virtuelle');
								const disabled =
									isProductionRegionSelected && !isVirtualCapacity;
								const isCapacExisting = Object.values(data.values).some(
									(v) => v.value !== null
								);

								return (
									<tr key={rowIndex}>
										<td style={{ width: 'min-content' }}>
											<Row style={{ gap: '.5rem' }}>
												{data.epv_family_lvl2_ids.length > 0 && (
													<Icon
														icon='arrow-rotate-left'
														style={{
															cursor: 'pointer',
														}}
														onClick={async () => {
															if (
																await confirmModal({
																	content:
																		'Are you sure you want to reset this capacity ?',
																})
															) {
																resetCapacity(data.epv_family_lvl2_ids);
															}
														}}
														color={theme.background.primary}
													/>
												)}
												{isVirtualCapacity && (
													<Icon
														icon='minus'
														onClick={async () => {
															if (
																await confirmModal({
																	content: `Are you sure you want to reset this capacity ?`,
																})
															) {
																deleteCapacityUpdate({
																	capacity_type: data.capacity_type,
																	epv_families: data.epv_family_lvl2_ids,
																});
															}
														}}
														style={{
															cursor: 'pointer',
														}}
														color={theme.background.primary}
													/>
												)}
												{rowIndex === scenarioData!.length - 1 &&
													isAdding &&
													data.capacity_type &&
													data.epv_family_lvl2_ids.length > 0 && (
														<Icon
															icon='check'
															onClick={() => {
																createCapacity({
																	capacity_type: data.capacity_type,
																	epv_family_ids: data.epv_family_lvl2_ids,
																});
															}}
															style={{
																cursor: 'pointer',
															}}
															color={theme.background.primary}
														/>
													)}
											</Row>
										</td>
										<td>
											{row?.name ? (
												<span>{row?.name}</span>
											) : (
												<SelectField
													options={Object.entries(capacityTypes ?? {}).map(
														([key, value]) => ({
															label: value.name,
															value: key,
														})
													)}
													value={data.capacity_type}
													setValue={(v) => {
														setScenarioData((_) =>
															produce(_, (draft) => {
																const scnenario = draft[rowIndex];
																scnenario.capacity_type = v as keyof TProcess;
															})
														);
													}}
													variant='primary'
												/>
											)}
										</td>
										<td className='disabled'>{capacityType?.unit}</td>
										<Tippy
											content={getEpvFamiliesByIds(
												data.epv_family_lvl2_ids
											)?.join(', ')}>
											<td
												className={joinClassNames(
													isCapacExisting && 'disabled'
												)}>
												<SelectField
													options={getEpvFamiliesDependingOnCapacityType(
														data.capacity_type as keyof TProcess
													)}
													multiple
													show='value'
													value={data.epv_family_lvl2_ids}
													setValue={(v) => {
														setScenarioData((_) =>
															produce(_, (draft) => {
																const scnenario = draft[rowIndex];
																scnenario.epv_family_lvl2_ids = v as number[];
															})
														);
													}}
													closeWhenSelect={false}
													variant='none'
													disabled={isCapacExisting}
													showValueIfOptionDoesntExist
												/>
											</td>
										</Tippy>
										{Object.entries(row.values).map(
											([year, values], colIndex) => {
												const yearDisabled = disabled || +year <= CURRENT_YEAR;
												return (
													<Tippy
														key={`${rowIndex}-${colIndex}`}
														content={values.origin}>
														<td
															className={joinClassNames(
																yearDisabled && 'disabled'
															)}>
															<NumField
																disabled={yearDisabled}
																value={values.value}
																setValue={(v) => {
																	updateCapacity({
																		year: parseInt(year),
																		capacity: v as number,
																		capacity_type: data.capacity_type,
																		epv_families: data.epv_family_lvl2_ids,
																	});
																}}
																round
															/>
														</td>
													</Tippy>
												);
											}
										)}
									</tr>
								);
							})}
						</tbody>
					</Table>
				)}
			</ScenarioContentContainer>
		</div>
	);
};

export default Capacity;
